// SF2 sessions and SF3 page views.
//
// One surface with both metrics rather than two that differ by a column. The
// views-to-sessions ratio is the part neither gives alone: sessions are unique
// visits, page views count every load, and the gap between them says whether a
// listing is being compared before a decision.
import React, { useState } from 'react';
import s from '../Visibility.module.css';
import { money, num } from '../format';

const Change = ({ value }) => {
  if (value == null) return <span className={s.flat}>—</span>;
  const up = value >= 0;
  return (
    <span className={up ? s.up : s.down}>
      {up ? '▲' : '▼'} {Math.abs(value * 100).toFixed(0)}%
    </span>
  );
};

const SIZES = [10, 25, 50];

export default function Traffic({ data }) {
  // The PRD is explicit that N is seller-controlled: a fixed cutoff hides
  // the product they opened the screen to find.
  const [top, setTop] = useState(10);
  if (!data) return <div className={s.empty}>Loading…</div>;
  if (!data.products?.length) {
    return (
      <div className={s.empty}>
        <p>Sales &amp; Traffic has not been collected for this window.</p>
        <p className={s.hint}>
          It carries each product&apos;s sessions and page views, and Amazon serves it to
          every seller — Brand Registry is not needed. Collect it from the Search Terms
          screen.
        </p>
      </div>
    );
  }

  const a = data.summary;
  // N is the seller's to choose: a fixed cutoff hides the product they opened
  // the screen to find.
  const shown = top === 'all' ? data.products : data.products.slice(0, top);

  return (
    <>
      <div className={s.stats}>
        <div className={s.stat}>
          <span className={s.statValue}>{num(a.sessions)}</span>
          <span className={s.statLabel}>sessions <Change value={a.change} /></span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{num(a.page_views)}</span>
          <span className={s.statLabel}>page views</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{a.views_per_session ?? '—'}</span>
          <span className={s.statLabel}>views per session</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{num(a.zero_traffic)}</span>
          <span className={s.statLabel}>with no traffic</span>
        </div>
      </div>

      <p className={s.hint} style={{ margin: '4px 0 14px' }}>
        {data.compared_to
          ? <>Compared with {data.compared_to}, on the {num(a.compared_on)} products present
            in both windows — {num(a.added)} were added and {num(a.dropped)} dropped, and
            counting those as traffic would put a change in the catalogue into a traffic
            figure. {num(a.falling)} products lost more than 10% of their sessions.</>
          : <>No earlier window is collected, so nothing can be compared yet.</>}
        {' '}A product above {a.ratio_threshold} views per session is being revisited before a
        decision rather than bought on the first visit — a content and pricing signal, not a
        traffic one.
      </p>

      {!!a.lost_all_traffic?.length && (
        <div className={s.section} style={{ marginBottom: 12 }}>
          <div className={s.sectionTitle}>
            Traffic stopped entirely ({a.lost_all_traffic.length})
          </div>
          <p className={s.hint}>
            These had sessions last window and none this one. A product going to exactly
            zero is usually out of stock or no longer listed rather than a listing that
            stopped working — worth checking before treating it as a visibility problem.
          </p>
          <div className={s.chipRow}>
            {a.lost_all_traffic.map((asin) => (
              <span key={asin} className={s.chipRival}>{asin}</span>
            ))}
          </div>
        </div>
      )}

      <div className={s.controls} style={{ marginBottom: 10 }}>
        <label className={s.field}>
          <span className={s.label}>Show</span>
          <select className={s.select} value={top}
                  onChange={(e) => setTop(e.target.value === 'all' ? 'all'
                                                                  : Number(e.target.value))}>
            {SIZES.map((n) => <option key={n} value={n}>Top {n}</option>)}
            <option value="all">All {data.products.length}</option>
          </select>
        </label>
      </div>

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Product</th>
              <th className={s.numeric}>Sessions</th>
              <th className={s.numeric}>Change</th>
              <th className={s.numeric}>Page views</th>
              <th className={s.numeric}>Views / session</th>
              <th className={s.numeric}>Units</th>
              <th className={s.numeric}>Revenue</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.asin}>
                <td className={s.asin}>{p.asin}</td>
                <td className={s.numeric}>{num(p.sessions)}</td>
                <td className={s.numeric}><Change value={p.change} /></td>
                <td className={s.numeric}>{num(p.page_views)}</td>
                <td className={s.numeric}>
                  {p.views_per_session ?? '—'}
                  {p.comparison_shopping && <span className={s.tag}>compared</span>}
                </td>
                <td className={s.numeric}>{num(p.units_ordered)}</td>
                <td className={s.numeric}>{p.revenue == null ? '—' : money(p.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </>
  );
}
